import { Fragment, type ReactElement, useMemo } from "react"

import { useTranslation } from "react-i18next"

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt"
import { Grid } from "@mui/material"
import { Button, Skeleton, Typography } from "@synapse-analytics/synapse-ui"
import moment from "moment"

import { KonanEmptyState } from "../../components/utility/KonanEmptyState"
import { InsightList } from "../../types/generated/api/InsightList"
import { Report } from "../../types/generated/api/Report"
import type { SimulationJobRetrieve } from "../../types/generated/api/SimulationJobRetrieve"
import { ReportV0 } from "./ReportV0"
import { ReportV1 } from "./ReportV1"

import styles from "./Simulations.module.scss"

type SimulationReport = {
  simulationJob?: SimulationJobRetrieve
  reportData: Report | undefined
  isContainerLoading: boolean
  cancelSimulation: () => void
  isCancelMutationLoading: boolean
}

/**
 * SimulationReport component
 * @param {SimulationReport} props - Props containing simulation job, report data, and loading state
 * @return {React.ReactElement} - Rendered component
 */
export function SimulationReport(props: SimulationReport): ReactElement {
  const { simulationJob, reportData, isContainerLoading, cancelSimulation, isCancelMutationLoading } = props

  const { t } = useTranslation("simulations", {
    keyPrefix: "simulationReport",
  })

  /**
   * Date range extractor in the following form {DD:MM:YYYY (start) -> DD:MM:YYYY (end)}
   * @return {React.ReactElement | undefined}
   */
  const reportCardDateRange = useMemo((): ReactElement | undefined => {
    if (simulationJob?.predictions_start_time && simulationJob?.predictions_end_time)
      return (
        <Grid container wrap="nowrap">
          <Grid item>
            <Typography variant="p" noWrap>
              {moment(simulationJob?.predictions_start_time).format("MMM DD YYYY")}
            </Typography>
          </Grid>
          <Grid item>
            <ArrowRightAltIcon fontSize="small" className={styles.dateIcon} />
          </Grid>
          <Grid item>
            <Typography variant="p" noWrap>
              {moment(simulationJob?.predictions_end_time).format("MMM DD YYYY")}
            </Typography>
          </Grid>
        </Grid>
      )
  }, [simulationJob?.predictions_end_time, simulationJob?.predictions_start_time])

  // Loading state JSX
  if (isContainerLoading) {
    return (
      <Fragment>
        <Grid container item xs={12} spacing={2} mb={2}>
          {Array.from({ length: 3 }, (_, i) => (
            <Grid item xs={12} lg={4} key={i}>
              <Skeleton height={"10px"} marginBottom={8} />
              <Skeleton height={"20px"} />
            </Grid>
          ))}
        </Grid>

        <Grid container item xs={12} spacing={2} mb={2}>
          {Array.from({ length: 3 }, (_, i) => (
            <Grid item xs={12} lg={4} key={i}>
              <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="flex-start"
                className={`${styles.skeletonContainerBase} full-height`}
              >
                <Skeleton width="100%" height={20} marginBottom={8} />
                <Skeleton width="75%" />
              </Grid>
            </Grid>
          ))}
        </Grid>

        {Array.from({ length: 2 }, (_, i) => (
          <Grid key={i} container className={styles.skeletonContainer}>
            <Skeleton width="100%" height={40} marginBottom={8} />
            <Skeleton width="75%" marginBottom={8} />
            <Skeleton width="60%" height={30} marginBottom={8} />
            <Skeleton width="30%" />
          </Grid>
        ))}
      </Fragment>
    )
  }

  // Failed State JSX
  if (
    reportData &&
    reportData.insights.length > 0 &&
    reportData?.insights.every((insight) => insight.status === "FAILED")
  ) {
    return (
      <Grid alignContent={"center"} height={"100%"}>
        <KonanEmptyState title={t("emptyState.failed.title")} subTitle={t("emptyState.failed.subtitle")} />
      </Grid>
    )
  }

  // Pending State JSX
  if (
    ["Running", "Pending"].includes(simulationJob?.status as string) ||
    [Report.status.PENDING, Report.status.PREPARATION, Report.status.RUNNING].includes(
      reportData?.status as Report.status,
    )
  ) {
    return (
      <Grid alignContent={"center"} height={"100%"}>
        <KonanEmptyState
          title={t("emptyState.generating.title")}
          subTitle={t("emptyState.generating.subtitle")}
          actionButtons={[
            <Button
              variant="dangerous"
              disabled={isCancelMutationLoading}
              isLoading={isCancelMutationLoading}
              onClick={cancelSimulation}
            >
              {t("emptyState.generating.cancelSimulation")}
            </Button>,
          ]}
        />
      </Grid>
    )
  }

  // Report JSX
  if (reportData && simulationJob) {
    if (
      reportData.insights.length === 1 &&
      reportData.insights[0]?.type === InsightList.type.LEGACY_SIMULATION_REPORT
    ) {
      return (
        <ReportV0
          simulation={simulationJob}
          reportData={reportData}
          isReportResLoading={isContainerLoading}
          predictionDateRange={reportCardDateRange}
        />
      )
    } else {
      return (
        <ReportV1
          reportData={reportData}
          dataSetName={simulationJob.predictions_datafile?.name}
          predictionDateRange={reportCardDateRange}
          feedbackColumn={
            simulationJob.feedback_column
              ? simulationJob.feedback_column
              : simulationJob.predictions_end_time && simulationJob.predictions_start_time
                ? "Feedback"
                : undefined
          }
        />
      )
    }
  }

  // Empty State JSX
  return (
    <Grid alignContent={"center"} height={"100%"}>
      <KonanEmptyState title={t("emptyState.unavailable.title")} subTitle={t("emptyState.unavailable.subtitle")} />
    </Grid>
  )
}
